import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { history } from 'store/Store';
import { MainRoutePath } from 'library/Routes';
import {
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@material-ui/core';
import SicrediLogo from 'components/SicrediLogo/SicrediLogo';
import { InvertedChevronIcon, ChevronIcon, SyncIcon } from 'components/Icons';
import useStyle from './style';
import CustomRouterLink from 'components/CustomRouterLink/CustomRouterLink';

export function Subheader() {
  const classes = useStyle();
  const goToHome = () => history.push(MainRoutePath);
  return (
    <ListSubheader
      component="button"
      onClick={goToHome}
      className={classes.subheader}
    >
      <SicrediLogo />
    </ListSubheader>
  );
}

interface ItemProps {
  path: string;
  text: string;
  icon: React.ReactNode;
  closeMenu: Function;
}

export function Item({ path, text, icon, closeMenu }: ItemProps) {
  const classes = useStyle();
  const [currentPath, setCurrentPath] = useState<string>(
    window.location.pathname
  );
  const onChangedLocation = () => setCurrentPath(window.location.pathname);
  const isCurrent = useMemo(() => path === currentPath, [currentPath, path]);
  const currentClass = useMemo(() => (isCurrent ? classes.itemCurrent : ''), [
    isCurrent,
    classes,
  ]);

  useEffect(() => {
    const urlChangeUnlisten = history.listen(onChangedLocation);
    return () => urlChangeUnlisten();
  }, []);

  return (
    <ListItem
      component={CustomRouterLink}
      to={path}
      onClick={() => closeMenu()}
      className={[classes.item, currentClass].join(' ').trim()}
      button
    >
      <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
      <ListItemText className={classes.itemText} primary={text} />
    </ListItem>
  );
}

interface SubmenuProps {
  name: string;
  icon: React.ReactNode;
  items?: ItemProps[];
  isLoading?: boolean;
  isCurrent?: boolean;
  isAssociado?: boolean;
}

export function Submenu({
  name,
  icon,
  items,
  isLoading,
  isCurrent = false,
  isAssociado = false,
}: SubmenuProps) {
  const classes = useStyle();
  const [isOpen, setOpen] = useState<boolean>(isCurrent || isAssociado);
  const handleToggle = useCallback(() => setOpen(!isOpen), [isOpen]);
  const rightIcon = useMemo(() => {
    if (isLoading) return <SyncIcon animated />;
    return isOpen ? <ChevronIcon /> : <InvertedChevronIcon />;
  }, [isLoading, isOpen]);

  return (
    <React.Fragment>
      <ListItem
        className={[classes.item, isCurrent ? classes.itemCurrent : '']
          .join(' ')
          .trim()}
        component="button"
        onClick={handleToggle}
        button
      >
        <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
        <ListItemText className={classes.itemText} primary={name} />
        {rightIcon}
      </ListItem>
      <Collapse in={isOpen} timeout="auto">
        <List component="div" className={classes.submenu} disablePadding>
          {(items || []).map(entry => (
            <Item key={entry.path} {...entry} />
          ))}
        </List>
      </Collapse>
    </React.Fragment>
  );
}
